import {
  cloneElement,
  MouseEvent,
  ReactElement,
  SyntheticEvent,
  useCallback,
  useState,
} from 'react';

import styled from '@emotion/styled';
import { iconButtonClasses } from '@mui/material';
import MuiMenu, { menuClasses } from '@mui/material/Menu';
import { useTranslations } from 'next-intl';
import { useCopyToClipboard } from 'usehooks-ts';

import { IconButton, showSnackbar } from '@web/ui/components';
import { HyperlinkIcon, TwitterIcon } from '@web/ui/icons';
import { getStateLayerColor } from '@web/ui/theme/utils';
import { getShareTwitterLink } from '@web/utils/socials';

const Menu = styled(MuiMenu)`
  .${menuClasses.paper} {
    margin-top: -${({ theme }) => theme.spacing('0500')};
    background-color: ${({ theme }) => theme.color.surfaceContainer};
    border-radius: ${({ theme }) => theme.shape.corner['1000']};
    box-shadow: ${({ theme }) => theme.elevation.level1};
  }

  .${menuClasses.list} {
    padding: ${({ theme }) => theme.spacing('1500')};
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing('1500')};
  max-width: 172px;
`;

const Title = styled.p`
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.bold};
  font-size: ${({ theme }) => theme.typescale.size['2']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing('1500')};
`;

const Action = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('1500')};

  & .${iconButtonClasses.root} {
    padding: 14px;
    width: fit-content;
    background-image: ${getStateLayerColor('onSurface', 'semiTransparent')};
  }
`;

const ActionName = styled.p`
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['0750']};
`;

export interface IThemeMenuProps {
  Trigger: ReactElement;
  title?: string;
  link?: string;
  twitter?: string;
}

export function ShareMenu({ Trigger, title, link }: IThemeMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleOpen = (event: MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const isOpen = Boolean(anchorEl);
  const [, copy] = useCopyToClipboard();

  const t = useTranslations();

  const handleCopyLink = useCallback(
    (e: SyntheticEvent<HTMLSpanElement>) => {
      e.preventDefault();
      showSnackbar(t('SHARE_MENU_LINK_COPIED'));
      copy(link || window.location.href);
    },
    [window.location.href],
  );

  return (
    <>
      {cloneElement(Trigger, { onClick: handleOpen })}
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <ContentContainer>
          <Title>{title || t('SHARE_MENU_TITLE')}</Title>
          <Actions>
            <Action>
              <IconButton
                href={getShareTwitterLink(link || window.location.href, {})}
                rel="noreferrer"
                target="_blank"
                onClick={(e) => e.stopPropagation()}
              >
                <TwitterIcon color="onSurface" />
              </IconButton>
              <ActionName>{t('SHARE_MENU_TWITTER')}</ActionName>
            </Action>
            <Action>
              <IconButton onClick={handleCopyLink}>
                <HyperlinkIcon color="onSurface" />
              </IconButton>
              <ActionName>{t('SHARE_MENU_COPY')}</ActionName>
            </Action>
          </Actions>
        </ContentContainer>
      </Menu>
    </>
  );
}
